import React from 'react';
import { SelectOptions } from '../../../utils/types/react_select_types';
import { ReportingFilters } from '../../../utils/types/reporting/reporting_types';
import { CustomSelect } from '../../custom_react_select/custom_select';
import { DatePicker } from '../../input/date_picker/date_picker';
import { Button } from '../Common/Buttons/Button';

export interface ReportingFilterBarProps {
  filters: ReportingFilters;
  onChange: (filters: ReportingFilters) => void;
  departments: string[];
  jobTitles: string[];
}

const toOptions = (values: string[]): SelectOptions[] =>
  values.map((value) => ({ value, label: value }));

export const ReportingFilterBar: React.FC<ReportingFilterBarProps> = ({
  filters,
  onChange,
  departments,
  jobTitles,
}) => {
  const departmentOptions = toOptions(departments);
  const jobTitleOptions = toOptions(jobTitles);

  const handleDateChange = (range: {
    startDate: string | null;
    endDate: string | null;
  }) => {
    onChange({
      ...filters,
      dateRange: {
        startDate: range.startDate ? new Date(range.startDate) : null,
        endDate: range.endDate ? new Date(range.endDate) : null,
      },
    });
  };

  const handleDepartmentChange = (selected: SelectOptions[] | null) => {
    onChange({
      ...filters,
      departments: selected ? selected.map((option) => option.value) : [],
    });
  };

  const handleJobTitleChange = (selected: SelectOptions[] | null) => {
    onChange({
      ...filters,
      jobTitles: selected ? selected.map((option) => option.value) : [],
    });
  };

  const handleClear = () => {
    onChange({
      dateRange: { startDate: null, endDate: null },
      departments: [],
      jobTitles: [],
      locations: [],
    });
  };

  const hasActiveFilters =
    filters.departments.length > 0 ||
    filters.jobTitles.length > 0 ||
    filters.locations.length > 0 ||
    !!filters.dateRange.startDate ||
    !!filters.dateRange.endDate;

  return (
    <div className='flex flex-wrap items-end gap-3 border-b border-gray-100 bg-white px-6 py-3'>
      <div className='w-full sm:w-56'>
        <DatePicker
            label='Date Range'
            startDate={filters.dateRange.startDate}
            endDate={filters.dateRange.endDate}
            onChange={handleDateChange}
        />
      </div>
      <div className='w-full sm:w-56'>
        <CustomSelect
            name='departments'
            label='Department'
            placeholder='All departments'
            isMulti
            clearable
            options={departmentOptions}
            value={departmentOptions.filter((option) =>
              filters.departments.includes(option.value),
            )}
            onChange={handleDepartmentChange}
        />
      </div>
      <div className='w-full sm:w-56'>
        <CustomSelect
            name='jobTitles'
            label='Job Title'
            placeholder='All job titles'
            isMulti
            clearable
            options={jobTitleOptions}
            value={jobTitleOptions.filter((option) =>
              filters.jobTitles.includes(option.value),
            )}
            onChange={handleJobTitleChange}
        />
      </div>
      <div className='w-full sm:ml-auto sm:w-auto'>
        <Button
            label='Clear filters'
            variant='link'
            onClick={handleClear}
            disabled={!hasActiveFilters}
        />
      </div>
    </div>
  );
};
